import { useEffect } from 'preact/hooks';

/**
 * Fires Ontraport's page-view tracker (`mrtracking()`) against a per-course
 * OP account ID. Used on campaign checkout and provision pages where the
 * course lives in a different OP account than the tenant (Story Coach vs
 * Mirasee).
 *
 * Why fire here and not rely on the server-side middleware:
 *   - The middleware fires once on initial HTML load. SPA route changes
 *     (e.g., campaign checkout → /courses/:id/provision) don't re-load
 *     the HTML, so `mrtracking()` never fires for the new URL. OP's
 *     cart-abandonment goals key on URL visits, so the thank-you visit
 *     must fire explicitly on route entry.
 *   - For campaign pages served directly by URL, the middleware already
 *     routed the fire to the right account (via slug → course lookup);
 *     this hook's extra fire on mount produces a duplicate visit to the
 *     same account, which OP dedupes by session.
 */
export function useOntraportAccountOverride(accountId: string | undefined) {
  useEffect(() => {
    if (!accountId) {
      return;
    }
    try {
      const w = window as any;
      w._mri = accountId;
      w._mr_domain = w._mr_domain || 'tracking.ontraport.com';
      if (typeof w.mrtracking === 'function') {
        w.mrtracking();
      }
    } catch {
      // Tracking-script overrides must never block the page.
    }
  }, [accountId]);
}
